import { useEffect, useMemo, useState } from 'react';
import type { RoleDefinition } from '../../../app/roles';
import { useRepositories } from '../../../local/provider';
import type { Asset, AssetQuery, CampusScaleSummary } from '../../../domain';
import { DepthBrief, PerspectiveFieldGuide, defineDepthContent, useDepth, useDepthValue } from '../../../presentation';
import { AssetTable } from './AssetTable';
import { AssetDrawer } from './AssetDrawer';
import './inventory.css';

export type AssetJoined = Asset & {
  assigneeName?: string;
  departmentName?: string;
  locationName?: string;
  isCompliant?: boolean;
};

const assetDepthContent = defineDepthContent({
  overview: {
    summary: 'Find who holds each device, where it is located, and which records need attention.',
    title: 'Custody and attention',
    signals: ['Assignment and location', 'Condition and availability', 'Immediate follow-up state'] as const,
  },
  operations: {
    summary: 'Search assignment, verification, warranty, compliance, and lifecycle state across local inventory.',
    title: 'Inventory operations',
    signals: ['Lifecycle and saved views', 'Verification and compliance', 'Exportable operational records'] as const,
  },
  architecture: {
    summary: 'Inspect stable asset IDs, repository-backed assignments, location relationships, and permitted technical identifiers.',
    title: 'Inventory relationships',
    signals: ['Asset and assignment identifiers', 'Person, department, and room links', 'Dexie data behind repository boundaries'] as const,
  },
});

export function AssetInventory({ role, onViewDetails }: { role: RoleDefinition, onViewDetails: (id: string) => void }) {
  const repo = useRepositories();
  const { depth } = useDepth();
  const lens = useDepthValue(assetDepthContent);
  const [assets, setAssets] = useState<AssetJoined[]>([]);
  const [scale, setScale] = useState<CampusScaleSummary | null>(null);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [view, setView] = useState('all');
  const [query, setQuery] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  useEffect(() => {
    const timeout = window.setTimeout(() => {
    async function load() {
      const filters: AssetQuery = {
        offset: page * pageSize,
        limit: pageSize,
        query,
        state: view === 'assigned' ? 'assigned' : undefined,
        attentionOnly: view === 'attention',
        category: view === 'mobile' ? 'mobile-phone' : undefined,
      };
      const [assetPage, campusScale] = await Promise.all([
        repo.inventory.getAssetPage(filters),
        repo.inventory.getCampusScaleSummary(),
      ]);

      const joined = await Promise.all(assetPage.items.map(async (asset) => {
        let assigneeName, departmentName, locationName;
        // Mock MDM compliance check based on some logic (e.g., if active and has a serial)
        const isCompliant = asset.state === 'assigned' && !!asset.serialNumber;

        // Get Assignment
        const assignments = await repo.inventory.getAssignmentsByAsset(asset.id);
        const activeAssignment = assignments[0]; // Simplified

        if (activeAssignment) {
          const person = await repo.campus.getPerson(activeAssignment.personId);
          if (person) {
            assigneeName = `${person.firstName} ${person.lastName}`;
            if (person.departmentId) {
              const dept = await repo.campus.getDepartment(person.departmentId);
              departmentName = dept?.name;
            }
          }
        }

        if (asset.locationId) {
          const room = await repo.campus.getRoom(asset.locationId);
          if (room) {
            locationName = room.name;
          }
        }

        return { ...asset, assigneeName, departmentName, locationName, isCompliant };
      }));
      setAssets(joined);
      setFilteredTotal(assetPage.filteredTotal);
      setTotal(assetPage.total);
      setScale(campusScale);
    }
    void load();
    }, 160);
    return () => window.clearTimeout(timeout);
  }, [page, query, repo, view]);

  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      return view !== 'attention' || asset.health !== 'healthy';
    });
  }, [assets, view]);

  const exportAudit = () => {
    const header = ['Asset Tag', 'Device', 'Assignee', 'Department', 'State', 'Location'];
    const rows = filteredAssets.map((asset) => [
      asset.assetTag,
      asset.name,
      asset.assigneeName ?? '',
      asset.departmentName ?? '',
      asset.state,
      asset.locationName ?? '',
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'croc-ops-asset-audit.csv';
    anchor.click();
    URL.revokeObjectURL(url);
    setActionMessage(`Exported ${filteredAssets.length} records from the current page.`);
  };

  return (
    <div className="asset-inventory">
      <div className="asset-page-heading">
        <div>
          <p className="eyebrow">Inventory control</p>
          <h1>Asset Inventory</h1>
          <p>{lens.summary} {total.toLocaleString()} managed records are available.</p>
        </div>
        <span>Demonstration dataset</span>
      </div>
      <DepthBrief summary={lens.summary} signals={lens.signals} title={lens.title} />
      <PerspectiveFieldGuide surface="assets" />
      <header className="inventory-header">
        <div className="inventory-controls">
          <label className="view-selector">
            <span className="sr-only">Saved View</span>
            <select value={view} onChange={(event) => { setView(event.target.value); setPage(0); }}>
              <option value="all">All Assets</option>
              <option value="assigned">Assigned Assets</option>
              <option value="attention">Needs Attention</option>
              <option value="mobile">College Mobile Phones</option>
            </select>
          </label>
          <input
            aria-label="Filter asset inventory"
            className="global-filter"
            onChange={(event) => { setQuery(event.target.value); setPage(0); }}
            placeholder="Filter by tag, device, serial, or category"
            type="search"
            value={query}
          />
        </div>
      </header>

      {depth === 'overview' && (
        <section className="fleet-metrics fleet-metrics--3" aria-label="Asset outcomes">
          <div className="metric-card"><span className="metric-label">Ready to assign</span><strong className="metric-value">12 devices</strong></div>
          <div className="metric-card warning"><span className="metric-label">Needs follow-up</span><strong className="metric-value">18 records</strong></div>
          <div className="metric-card"><span className="metric-label">Custody coverage</span><strong className="metric-value">92% known</strong></div>
        </section>
      )}
      {depth === 'operations' && (
        <section className="fleet-metrics" aria-label="Operational asset metrics">
          <div className="metric-card"><span className="metric-label">Loaner Pool</span><strong className="metric-value">12 Available</strong></div>
          <div className="metric-card warning"><span className="metric-label">MDM Non-Compliant</span><strong className="metric-value">4 Devices</strong></div>
          <div className="metric-card critical"><span className="metric-label">Expiring Warranties</span><strong className="metric-value">2 This Month</strong></div>
          <div className="metric-card"><span className="metric-label">Unverified</span><strong className="metric-value">18 &gt; 90 Days</strong></div>
        </section>
      )}
      {depth === 'architecture' && (
        <section className="fleet-metrics fleet-metrics--3" aria-label="Asset record relationships">
          <div className="metric-card"><span className="metric-label">Asset records</span><strong className="metric-value">{total.toLocaleString()} local</strong></div>
          <div className="metric-card"><span className="metric-label">Relationship types</span><strong className="metric-value">4 explicit</strong></div>
          <div className="metric-card"><span className="metric-label">Data boundary</span><strong className="metric-value">Repository API</strong></div>
        </section>
      )}

      <div className="inventory-result-note">
        <span>
          {filteredTotal
            ? `${(page * pageSize + 1).toLocaleString()}–${Math.min((page + 1) * pageSize, filteredTotal).toLocaleString()} of ${filteredTotal.toLocaleString()} matching records`
            : `0 of ${total.toLocaleString()} records`}
          {scale ? ` · ${scale.identifiableDevices.toLocaleString()} identifiable campus devices` : ''}
        </span>
        {actionMessage && <span aria-live="polite">{actionMessage}</span>}
      </div>

      <AssetTable
        assets={filteredAssets}
        onAction={setActionMessage}
        onExport={exportAudit}
        onSelect={setSelectedAssetId}
        role={role}
        depth={depth}
      />

      <nav className="inventory-pagination" aria-label="Asset inventory pages">
        <button
          className="button"
          disabled={page === 0}
          onClick={() => setPage((current) => Math.max(0, current - 1))}
          type="button"
        >
          Previous
        </button>
        <span>Page {filteredTotal ? page + 1 : 0} of {Math.ceil(filteredTotal / pageSize)}</span>
        <button
          className="button"
          disabled={(page + 1) * pageSize >= filteredTotal}
          onClick={() => setPage((current) => current + 1)}
          type="button"
        >
          Next
        </button>
      </nav>

      <AssetDrawer
        assetId={selectedAssetId}
        onClose={() => setSelectedAssetId(null)}
        role={role}
        repo={repo}
        onViewDetails={onViewDetails}
      />
    </div>
  );
}
